/**
 * SharedDynamicViewPage - Public view for shared dynamic views.
 * Renders dynamic view content in a read-only format.
 */

import { DynamicViewRenderer } from '@/components/dynamic-views/DynamicViewRenderer';
import type { DynamicView, ViewDefinition } from '@/types/dynamic_views';
import type { SharedDynamicViewData } from '@/types/share';

interface SharedDynamicViewPageProps {
  data: SharedDynamicViewData;
}

export function SharedDynamicViewPage({ data }: SharedDynamicViewPageProps) {
  // Create a minimal DynamicView object from SharedDynamicViewData
  // to pass to the renderer
  const view: DynamicView = {
    id: data.id,
    title: data.title,
    description: data.description,
    view_type: 0, // Not needed for rendering
    view_type_display: { name: '', description: '' },
    conversation: 0, // Not needed for rendering
    view_definition: data.view_definition as unknown as ViewDefinition,
    interaction_data: {},
    status: data.status as DynamicView['status'],
    version_number: data.version_number,
    is_current_version: true,
    component_count: 0,
    has_versions: false,
    version_count: 1,
    views_count: 0,
    interaction_count: 0,
    created_at: data.created_at,
    updated_at: data.created_at,
  };

  return (
    <div className='min-h-full bg-bg-primary flex flex-col'>
      {/* Header with logo */}
      <header className='border-b border-border-secondary px-6 py-4'>
        <a href='https://ahoy.ai'>
          <img
            src='/ahoy-logo-with-text-black.svg'
            alt='Ahoy'
            className='h-6 dark:invert'
          />
        </a>
      </header>

      {/* Shared dynamic view content */}
      <main className='flex-1 max-w-4xl mx-auto w-full px-6 py-8'>
        {/* View title */}
        <h1 className='text-2xl font-semibold text-fg-primary mb-2'>
          {data.title || 'Shared View'}
        </h1>
        {data.description && (
          <p className='text-fg-secondary mb-6'>{data.description}</p>
        )}

        {/* Dynamic view content - force light mode for consistent table rendering */}
        <div
          className='bg-bg-secondary border border-border-secondary rounded-lg p-6'
          data-theme='light'
        >
          <DynamicViewRenderer view={view} interactive={false} />
        </div>
      </main>

      {/* Footer with Ahoy branding */}
      <footer className='border-t border-border-secondary px-6 py-4 text-center'>
        <p className='text-sm text-fg-tertiary'>
          Shared via{' '}
          <a
            href='https://ahoy.ai'
            className='text-brand-primary hover:underline'
          >
            Ahoy
          </a>
        </p>
      </footer>
    </div>
  );
}
